import React, { useEffect, useState } from 'react';
import { BookModel } from '../../../interfaces/book.interface';
import { TeacherClassroomStudent } from '../pages/TeacherClassroomStudents';
import { computeBookCompleteness, getIncompleteBadge } from '../../../helper/bookCompleteness';
import { Toast } from '../../../components/Common/Toast';

export interface AssignBooksPreOrderProps {
  onClickContinue: (studentBooks: Record<string, string>) => void;
  studentPreOrders: { user: TeacherClassroomStudent; books: BookModel[] }[];
  studentPreOrderBook: Record<string, string> | null;
}

const AssignBooksPreOrder: React.FC<AssignBooksPreOrderProps> = ({
  onClickContinue,
  studentPreOrders,
  studentPreOrderBook,
}: AssignBooksPreOrderProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [selectedBooks, setSelectedBooks] = useState<{ [key: string]: string }>({});

  // Only complete (print-ready) books may be assigned/shipped.
  const isShippable = (book: BookModel): boolean => computeBookCompleteness(book).isComplete;

  useEffect(() => {
    if (studentPreOrders.length > 0) {
      const initialSelectedBooks: { [key: string]: string } = {};
      studentPreOrders.forEach((student) => {
        const studentId = student.user.id || '';
        // Honour a previously-saved choice only if that book is still shippable.
        const savedId = studentPreOrderBook ? studentPreOrderBook[studentId] : '';
        const savedBook = savedId ? student.books.find((b) => b.bookId === savedId) : undefined;
        const defaultBook =
          savedBook && isShippable(savedBook)
            ? savedBook
            : student.books.find((b) => isShippable(b));
        initialSelectedBooks[studentId] = defaultBook?.bookId || '';
      });
      setSelectedBooks(initialSelectedBooks);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentPreOrders, studentPreOrderBook]);

  const handlePanelClick = (idx: number) => {
    if (openIndex === idx) {
      return;
    }
    setOpenIndex(openIndex === idx ? null : idx);
  };

  const handleBookSelect = (student: TeacherClassroomStudent, book: string) => {
    const studentName = student.id || '';
    setSelectedBooks((prev) => ({ ...prev, [studentName]: book }));
  };

  const handleContinue = () => {
    // Count pre-order students with no shippable book assigned. We still proceed
    // (the server skips incomplete books and reports them), but warn the teacher first.
    const unresolved = studentPreOrders.filter(
      (student) => !selectedBooks[student.user.id || ''],
    ).length;
    if (unresolved > 0) {
      Toast(
        `${unresolved} pre-order student${unresolved === 1 ? '' : 's'} have no completed book and will be skipped. Finish generating their images to ship them.`,
        true,
      );
    }
    onClickContinue(selectedBooks);
  };

  return (
    <div className="w-full">
      <h1 className="flex justify-center text-lg font-bold mb-4">Assign Books for Pre-Ordered Students</h1>
      <p className="text-sm text-gray-600 text-center px-6 mb-4">
        Pick which story each pre-paid student receives. All of a student's pre-ordered copies will be of the single
        book you select here.
      </p>
      <div className="space-y-6 max-h-screen md:max-h-[450px] md:w-[450px] overflow-y-auto px-4">
        {studentPreOrders.map((student, idx) => (
          <div key={idx} className="w-full border border-secondaryStoryGrayBorder rounded-xl shadow-sm bg-white">
            <div
              className="w-full flex items-center justify-between px-4 py-2 cursor-pointer"
              onClick={() => handlePanelClick(idx)}
            >
              <div className="flex items-center">
                <span className="text-xl font-bold text-secondaryStoryLightBlueText mr-2">
                  {student.user.loginName}
                </span>
                <span className="text-xs ms:text-base text-secondaryStoryLightBlueText">
                  ({student.user.preOrderStudentBookCount || 1} Pre-Order)
                </span>
              </div>
              <span>
                {openIndex === idx ? (
                  <svg
                    width="28"
                    height="28"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    className="text-black"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 15l-7-7-7 7" />
                  </svg>
                ) : (
                  <svg
                    width="28"
                    height="28"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    className="text-black"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 9l7 7 7-7" />
                  </svg>
                )}
              </span>
            </div>
            {openIndex === idx && (
              <div className="w-full px-4 py-3 border-t border-secondaryStoryGrayBorder flex flex-wrap gap-x-12 gap-y-4">
                {!student.books.some((b) => isShippable(b)) && (
                  <p className="min-w-full text-sm text-storyerror font-medium">
                    No completed books — finish generating images before shipping.
                  </p>
                )}
                {student.books.map((book) => {
                  const completeness = computeBookCompleteness(book);
                  const incompleteLabel = getIncompleteBadge(completeness);
                  return (
                    <label
                      key={book.bookId}
                      title={incompleteLabel ? completeness.reasons.join(', ') : undefined}
                      className={`flex items-center space-x-2 text-base min-w-full ${
                        completeness.isComplete ? '' : 'opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <input
                        type="radio"
                        name={book.bookId}
                        value={book.bookId}
                        disabled={!completeness.isComplete}
                        checked={selectedBooks[student.user.id || ''] === book.bookId!}
                        onChange={() => handleBookSelect(student.user, book.bookId!)}
                        className="accent-secondaryStoryOptionalLightBlueText w-4 h-4 disabled:cursor-not-allowed"
                      />
                      <span>{book.title}</span>
                      {incompleteLabel && (
                        <span className="text-xs font-medium text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">
                          {incompleteLabel}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap justify-end gap-2 md:flex-nowrap mt-5 px-14 sm:px-18 lg:px-22">
        <button
          type="button"
          className={`px-6 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center`}
          onClick={handleContinue}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default AssignBooksPreOrder;
